import { makeAutoObservable, runInAction } from 'mobx'
import type { Notify } from '@/Ship/Contracts/Notify'
import { parseApiError } from '@/Ship/Helpers/parseApiError'
import { InviteDriverTask } from '@/Containers/SettingSection/UserContainer/Tasks/InviteDriverTask'
import type { InviteDriverPayload } from '@/Containers/SettingSection/UserContainer/Contracts/InvitationPayload'

/**
 * Local store of `InvitationDriver` drawer.
 *
 * Driver invitation endpoint belongs to UserContainer's API contract
 * (`POST /services/users/driver-invitation`), but the form / drawer is
 * rendered here in DriverContainer (Stage Б of audit #22 moved it).
 * The store imports the Task and Payload type from UserContainer.
 */
export class InvitationDriverStore {
    loading = false
    successSubmit = false

    constructor() {
        makeAutoObservable(this)
    }

    resetSuccess(): void {
        this.successSubmit = false
    }

    private normalizeIri(value: any): string | null {
        if (value && typeof value === 'object') return value['@id'] ?? null
        return value ?? null
    }

    /**
     * Submit driver invitation form values.
     * Returns `true` on success — the view should close the drawer and reload list.
     * On 422 — extracts `violations` and feeds them to `formContext.setError`.
     */
    async submit(values: Record<string, any>, formContext: any, notify: Notify): Promise<boolean> {
        this.loading = true
        try {
            const payload: InviteDriverPayload = {
                lastName: values['lastName'],
                firstName: values['firstName'],
                middleName: values['middleName'] || null,
                phone: values['phone'],
                email: values['email'],
                driverLicenceNumber: values['driverLicenceNumber'] ?? null,
                isTrainee: values['isTrainee'] ?? false,
                carrier: this.normalizeIri(values['carrier']),
                position: this.normalizeIri(values['position']),
            }
            await InviteDriverTask(payload)
            notify.success('Водія успішно запрошено')
            runInAction(() => {
                this.successSubmit = true
            })
            return true
        } catch (err: any) {
            const parsed = await parseApiError(err)
            if (parsed.violations?.length) {
                parsed.violations.forEach((v) => formContext.setError(v.field, v.message))
                return false
            }
            notify.error(parsed.message ?? 'Не вдалося надіслати запрошення')
            return false
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }
}
